import { Effect } from 'postprocessing';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import {
  type WebGLRenderer,
  type WebGLRenderTarget,
  Uniform,
  Vector2,
} from 'three';

import fragmentShader from './cyclone-overlay.frag';

class CycloneEffectImpl extends Effect {
  private uTime: number = 0;

  constructor(opacity: number) {
    const res = new Vector2(window.innerWidth, window.innerHeight);

    super('CycloneEffect', fragmentShader, {
      uniforms: new Map<string, Uniform>([
        ['uResolution', new Uniform(res)],
        ['uTime', new Uniform(0)],
        ['uOpacity', new Uniform(opacity)],
      ]),
    });
  }

  get opacity() {
    return this.uniforms.get('uOpacity')!.value;
  }

  set opacity(n: number) {
    this.uniforms.get('uOpacity')!.value = n;
  }

  update(
    renderer: WebGLRenderer,
    inputBuffer: WebGLRenderTarget,
    deltaTime?: number
  ) {
    this.uTime += deltaTime || 0;
    this.uniforms.get('uTime')!.value = this.uTime;
  }

  setSize(width: number, height: number) {
    super.setSize(width, height);

    this.uniforms.get('uResolution')!.value.set(width, height);
  }
}

export interface CycloneEffectProps {
  opacity?: number;
}

export interface CycloneEffectRef {
  opacity: number;
}

export const CycloneEffect = forwardRef<CycloneEffectRef, CycloneEffectProps>(
  ({ opacity = 1 }, ref) => {
    const effectRef = useRef(new CycloneEffectImpl(opacity));

    useImperativeHandle(ref, () => ({
      get opacity() {
        return effectRef.current.opacity;
      },
      set opacity(n: number) {
        effectRef.current.opacity = n;
      },
    }));

    useEffect(() => {
      effectRef.current.opacity = opacity;
    }, [opacity]);

    return <primitive object={effectRef.current} />;
  }
);

CycloneEffect.displayName = 'CycloneEffect';
export default CycloneEffect;
